import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useFormik } from 'formik';
import { isFunction, setObjectValues } from 'utils';
import { mergeSchema } from 'utils/validation';

import type { IStep } from './types';

const useWizard = (
  steps: IStep[],
  validateOnNext: boolean,
  initialFormValues: any,
  onSubmit: (values: any, originalValues: any) => void,
  routePath: string,
  setTouchedOnMount,
) => {
  const { wizardStep } = useParams();
  const navigate = useNavigate();

  const activeStep = useMemo(
    () => (wizardStep ? steps.findIndex((step) => wizardStep === step.path.replace('/', '')) : 0),
    [wizardStep, steps],
  );

  const total = steps.length;
  const [currentStepIndex, setCurrentStepIndex] = useState(activeStep > -1 ? activeStep : 0);
  const isFirstStep: boolean = currentStepIndex === 0;
  const isLastStep: boolean = currentStepIndex >= total - 1;

  const currentStep: IStep = steps[currentStepIndex];

  const allValidationSchemas = useMemo(
    () =>
      steps
        .filter(({ validationSchema }) => validationSchema)
        .map(({ validationSchema }) => validationSchema),
    [steps],
  );

  const summarySchema = useMemo(() => mergeSchema(...allValidationSchemas), [allValidationSchemas]);

  const currentStepSchema = useMemo(
    () =>
      mergeSchema(
        ...steps
          .slice(0, currentStepIndex + 1)
          .filter(({ validationSchema }) => validationSchema)
          .map((step) => step.validationSchema),
      ),
    [steps, currentStepIndex],
  );

  const stepsInitialValues = useMemo(() => {
    return steps.reduce(
      (values, step) => (step.initialValues ? { ...values, ...step.initialValues } : values),
      {},
    );
  }, [steps]);

  const initialValues = useMemo(() => {
    return {
      ...summarySchema.cast(stepsInitialValues || {}, { assert: false }),
      ...summarySchema.cast(initialFormValues || {}, { assert: false }),
    };
  }, [initialFormValues, summarySchema, stepsInitialValues]);

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    validationSchema: isLastStep ? summarySchema : currentStepSchema,
    onSubmit: (data) => {
      onSubmit(
        summarySchema.cast(data, {
          stripUnknown: true,
          assert: false,
        }),
        data,
      );
    },
  });

  const { validateForm, values, setTouched } = formik;
  const firstInvalidStepIndex: number = useMemo(() => {
    let i = -1;
    for (const step of steps) {
      i++;
      if (!step.validationSchema) continue;
      try {
        step.validationSchema.validateSync(values);
      } catch (e) {
        return i;
      }
    }

    return -1;
  }, [steps, values]);

  useEffect(() => {
    if (activeStep === currentStepIndex) validateForm();
  }, [activeStep, currentStepIndex, validateForm]);

  // Set touched fields with errors
  useEffect(() => {
    if (setTouchedOnMount) {
      setTouched(setObjectValues(formik.errors, true));
    }
  }, [setTouchedOnMount, setTouched, formik.errors]);

  // Set current step based on the route
  useEffect(() => {
    if (activeStep !== currentStepIndex && activeStep !== -1) {
      setCurrentStepIndex(activeStep);
    }
  }, [activeStep, currentStepIndex]);

  // Redirect to the first step if the wizardStep doesn't exist
  useEffect(() => {
    if (activeStep === -1) {
      navigate(routePath);
    }
  }, [activeStep, navigate, routePath]);

  // Redirect to the invalid step if the wizardStep is different as the current one
  useEffect(() => {
    if (
      !formik.isValid &&
      firstInvalidStepIndex >= 0 &&
      firstInvalidStepIndex !== currentStepIndex &&
      firstInvalidStepIndex < currentStepIndex
    ) {
      const { path = '' } = steps[firstInvalidStepIndex];
      navigate(`${routePath}${path}`);
    }
  }, [
    firstInvalidStepIndex,
    formik.isValid,
    setTouched,
    formik.errors,
    steps,
    navigate,
    routePath,
    currentStepIndex,
  ]);

  const isNextDisabled: boolean = !formik.isValid;

  const goToPrev = useCallback(() => {
    const nextIndex = Math.max(0, currentStepIndex - 1);

    const { path } = steps[nextIndex];
    navigate(routePath + path);
  }, [currentStepIndex, navigate, steps, routePath]);

  const goToNext = useCallback(() => {
    const nextIndex = Math.min(currentStepIndex + 1, total - 1);

    const { path } = steps[nextIndex];
    navigate(routePath + path);
  }, [currentStepIndex, total, navigate, steps, routePath]);

  const { beforePrev, beforeNext } = currentStep;

  const handlePrev = useCallback(async () => {
    let isValid = true;

    if (isFunction(beforePrev)) {
      try {
        await beforePrev!(formik.values, formik, currentStepIndex);
      } catch (error) {
        isValid = false;
      }
    }

    if (isValid) {
      goToPrev();
    }
  }, [goToPrev, currentStepIndex, beforePrev, formik]);

  const handleNext = useCallback(async () => {
    let isValid = false;

    if (validateOnNext) {
      isValid = formik.isValid;
    }

    if (((validateOnNext && isValid) || !validateOnNext) && isFunction(beforeNext)) {
      try {
        await beforeNext!(formik.values, formik, currentStepIndex);
        isValid = true;
      } catch (error) {
        isValid = false;
      }
    }

    if (!isValid) return;

    if (isLastStep) {
      await formik.submitForm();
    } else {
      goToNext();
    }
  }, [goToNext, currentStepIndex, beforeNext, isLastStep, validateOnNext, formik]);

  return {
    formik,
    currentStep,
    isNextDisabled,
    isFirstStep,
    isLastStep,
    goToPrev,
    goToNext,
    handlePrev,
    handleNext,
  };
};

export { useWizard };
